import React, { useState, useEffect } from 'react';
import { useSurvey } from '../../context/SurveyContext';
import { useWizard } from '../../context/WizardContext';
import QuestionBuilder from './Builder/QuestionBuilder';
import SurveyPreview from './Preview/SurveyPreview';
import ResultsDashboard from './Results/ResultsDashboard';
import SurveyList from './Builder/SurveyList';
import IntegrationPrompt from './Builder/IntegrationPrompt';
import { DocumentUploadPanel, DocumentLibrary, SavedAnalysesList } from './Documents';
import DocumentAnalysis from './Documents/DocumentAnalysis';
import { FileText, Plus, BarChart3, Eye, ArrowLeft, Upload, FolderOpen } from 'lucide-react';
import './WizardF.css';

const DOCUMENTS_STORAGE_KEY = 'researchWizard_documents';

const WizardF = () => {
  const { 
    currentSurvey, 
    previewMode, 
    resultsMode,
    setPreviewMode,
    setResultsMode,
    wizardIntegrationChoice,
    setWizardIntegrationChoice,
    createNewSurvey 
  } = useSurvey();

  const { state } = useWizard();
  const [showSurveyList, setShowSurveyList] = useState(true);
  const [showIntegrationPrompt, setShowIntegrationPrompt] = useState(false);
  const [showDocumentUpload, setShowDocumentUpload] = useState(false);
  const [showDocumentLibrary, setShowDocumentLibrary] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [initialMethod, setInitialMethod] = useState(null);

  const hasWizardAData = state?.wizardA?.intake?.researchType || 
                         state?.wizardA?.philosophy || 
                         state?.wizardA?.strategy;
  const hasWizardBData = state?.wizardB?.researchQuestions?.length > 0 ||
                         state?.wizardB?.variables?.length > 0;
  const hasWizardCData = state?.wizardC?.constructs?.length > 0;

  const hasPreviousWizardData = hasWizardAData || hasWizardBData || hasWizardCData;

  useEffect(() => {
    if (currentSurvey) {
      setShowSurveyList(false);
    }
  }, [currentSurvey]);

  const handleNewSurvey = () => {
    if (hasPreviousWizardData && !wizardIntegrationChoice) {
      setShowIntegrationPrompt(true);
    } else {
      createNewSurvey();
      setShowSurveyList(false);
    }
  };

  const handleIntegrationChoice = (choice) => {
    setWizardIntegrationChoice(choice);
    setShowIntegrationPrompt(false);
    createNewSurvey();
    setShowSurveyList(false);
  };

  const handleBackToList = () => {
    setShowSurveyList(true);
    setPreviewMode(false);
    setResultsMode(false);
    setShowDocumentUpload(false);
    setShowDocumentLibrary(false);
    setSelectedDocument(null);
  };

  const handleDocumentParsed = (document) => {
    const saved = localStorage.getItem(DOCUMENTS_STORAGE_KEY);
    let documents = [];
    if (saved) {
      try {
        documents = JSON.parse(saved);
      } catch (e) {
        console.error('Failed to load documents:', e);
      }
    }
    documents.push(document);
    localStorage.setItem(DOCUMENTS_STORAGE_KEY, JSON.stringify(documents));
    
    setShowDocumentUpload(false);
    setSelectedDocument(document);
  };

  const handleSelectDocument = (document) => {
    setShowDocumentLibrary(false);
    setInitialMethod(null);
    setSelectedDocument(document);
  };

  const handleSelectAnalysis = (document, method) => {
    setInitialMethod(method);
    setSelectedDocument(document);
  };

  if (showIntegrationPrompt) {
    return (
      <IntegrationPrompt 
        onChoice={handleIntegrationChoice}
        onCancel={() => setShowIntegrationPrompt(false)}
        wizardAData={state?.wizardA}
        wizardBData={state?.wizardB}
        wizardCData={state?.wizardC}
      />
    );
  }

  if (showDocumentUpload) {
    return (
      <div className="wizard-f-container">
        <DocumentUploadPanel 
          onDocumentParsed={handleDocumentParsed}
          onCancel={() => setShowDocumentUpload(false)}
        />
      </div>
    );
  }

  if (showDocumentLibrary) {
    return (
      <div className="wizard-f-container">
        <DocumentLibrary 
          onSelectDocument={handleSelectDocument}
          onUploadNew={() => {
            setShowDocumentLibrary(false);
            setShowDocumentUpload(true);
          }}
          onBack={handleBackToList}
        />
      </div>
    );
  }

  if (selectedDocument) {
    return (
      <div className="wizard-f-container">
        <DocumentAnalysis 
          document={selectedDocument}
          initialMethod={initialMethod}
          onBack={() => {
            setSelectedDocument(null);
            setInitialMethod(null);
            setShowDocumentLibrary(true);
          }}
        />
      </div>
    );
  }

  if (showSurveyList) {
    return (
      <div className="wizard-f-container">
        <div className="wizard-f-header">
          <div className="wizard-f-title">
            <FileText size={28} />
            <h1>Survey Builder & Qualitative Analysis</h1>
          </div>
          <p className="wizard-f-subtitle">
            Create surveys, upload documents, and analyze text with advanced qualitative tools
          </p>
        </div>

        <div className="wizard-f-actions">
          <button className="btn-primary-large" onClick={handleNewSurvey}>
            <Plus size={20} />
            Create New Survey
          </button>
          <div className="actions-divider">
            <span>or</span>
          </div>
          <button className="btn-secondary-large" onClick={() => setShowDocumentUpload(true)}>
            <Upload size={20} />
            Upload Qualitative Data Document for Analysis
          </button>
          <button className="btn-outline-large" onClick={() => setShowDocumentLibrary(true)}>
            <FolderOpen size={20} />
            Document Library
          </button>
        </div>

        <SavedAnalysesList onSelectAnalysis={handleSelectAnalysis} />

        <SurveyList onSelectSurvey={() => setShowSurveyList(false)} />
      </div>
    );
  }

  if (previewMode) {
    return <SurveyPreview onBack={() => setPreviewMode(false)} />;
  }

  if (resultsMode) {
    return <ResultsDashboard onBack={() => setResultsMode(false)} />;
  }

  return (
    <div className="wizard-f-container">
      <div className="wizard-f-nav">
        <button className="btn-back" onClick={handleBackToList}>
          <ArrowLeft size={18} />
          All Surveys
        </button>
        <div className="wizard-f-nav-actions">
          <button 
            className="btn-secondary"
            onClick={() => setPreviewMode(true)}
            disabled={!currentSurvey?.questions?.length}
          >
            <Eye size={18} />
            Preview
          </button>
          <button 
            className="btn-secondary"
            onClick={() => setResultsMode(true)}
          >
            <BarChart3 size={18} />
            Results & Analytics
          </button>
        </div>
      </div>
      <QuestionBuilder />
    </div>
  );
};

export default WizardF;
